import React from 'react';
import { Button } from '../../Utility/Button';
import Input from '../../Utility/FormElements/Input';
import Checkbox from '../../Utility/FormElements/Checkbox';
import AdminContainer from '../../Utility/AdminContainer';
import AdminBox from '../../Utility/AdminBox';
import AdminBoxHeadline from '../../Utility/AdminBoxHeadline';
import TextareaAutosize from 'react-textarea-autosize';
import useKnowledgebase from '../../../Hooks/useKnowledgebase';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import * as PhosphorIcons from '@phosphor-icons/react';
import { Spinner } from '../../Utility/Spinner';
import { RootState, useAppSelector } from '../../../store';
import useKnowledgebaseSources from '../../../Hooks/useKnowledgebaseSources';
import Select from '../../Utility/FormElements/Select';
import PaginatedFileList from './components/PaginatedFileList';
import FileListItem from './components/FileListItem';
import { Document } from '../../../Types';
import { PaginationListType } from '../../../Types/PaginationParams';

const Knowledgebase: React.FC = () => {
    const { id } = useParams();

    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);

    const {
        isEditMode,
        isLoaded,
        isSaving,
        formData,
        i18n,
        handleReembedDocument,
        handleSubmit,
        handleChange,
        knowledgebaseSnapshot,
    } = useKnowledgebase();

    const {
        sourceProcessors,
        tempSource,
        isLoadedSources,
        isEditingSource,
        knowledgebaseSources,
        handleTempSource,
        handleEditSource,
        handleCloseSource,
        handleSourceChange,
        handleDocumentUpload,
        handleRemoveExistingDocument,
        sourceActions,
        sourceHasChanges,
        getKnowledgebaseDocuments,
    } = useKnowledgebaseSources();

    const onDrop = (acceptedFiles: File[]) => {
        handleDocumentUpload(acceptedFiles);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
    });

    return (
        <div className="admin-container flex relative flex-col">
            <AdminContainer>
                {isEditingSource.status &&
                    (() => {
                        const editingSource = knowledgebaseSources.find((source) => source.id === isEditingSource.id);

                        if (!editingSource) return null;

                        return (
                            <AdminBox>
                                <div className="flex border-b border-layout-borderColor mb-6 pb-3">
                                    <Button
                                        variant={'secondaryBigRound'}
                                        onClick={() => handleCloseSource(editingSource)}
                                    >
                                        <PhosphorIcons.ArrowLeftIcon />
                                    </Button>

                                    <div className="h-[50px] flex items-center mb-6 pl-5">
                                        <h4 className="p-0 m-0">
                                            {i18n.translate({
                                                id: 'source.edit',
                                                sourceName: 'KnowledgebaseSources',
                                                params: { name: editingSource.name },
                                            })}{' '}
                                        </h4>
                                    </div>
                                </div>

                                <h4 className="mb-5">
                                    {i18n.translate({
                                        id: 'headline.generalSettings',
                                        sourceName: 'KnowledgebaseSources',
                                    })}
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-layout-borderColor mb-6 pb-3">
                                    <div>
                                        <label htmlFor="sourceType" className="block mb-2">
                                            {i18n.translate({
                                                id: 'field.sourceType',
                                                sourceName: 'KnowledgebaseSources',
                                            })}
                                        </label>
                                        <Select id="sourceType" onChange={() => {}} value={editingSource.type} disabled>
                                            <option key={editingSource.type} value={editingSource.type}>
                                                {i18n.translate({
                                                    id: `field.sourceType.option.${editingSource.type}`,
                                                    sourceName: 'KnowledgebaseSources',
                                                })}
                                            </option>
                                        </Select>
                                        <p className="text-gray-400 mt-3">
                                            {i18n.translate({
                                                id: 'hint.sourceType',
                                                sourceName: 'KnowledgebaseSources',
                                            })}
                                        </p>
                                    </div>

                                    <div>
                                        <label htmlFor="sourceName" className="block mb-2">
                                            {i18n.translate({
                                                id: 'field.sourceName',
                                                sourceName: 'KnowledgebaseSources',
                                            })}
                                        </label>
                                        <Input
                                            inputType="text"
                                            value={editingSource.name}
                                            onChange={(value) => handleSourceChange(editingSource, { name: value })}
                                            id="sourceName"
                                        />
                                    </div>
                                </div>

                                <h4 className="mb-5">
                                    {i18n.translate({
                                        id: 'headline.connectionSettings',
                                        sourceName: 'KnowledgebaseSources',
                                    })}
                                </h4>
                                <div className="border-b border-layout-borderColor mb-6 pb-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {editingSource.type &&
                                            Object.keys(
                                                sourceProcessors[editingSource.type as keyof typeof sourceProcessors]
                                                    .configuration,
                                            ).map((key) => {
                                                const sourceConfiguration =
                                                    sourceProcessors[
                                                        editingSource.type as keyof typeof sourceProcessors
                                                    ].configuration[key];
                                                switch (sourceConfiguration.type) {
                                                    case 'text':
                                                    case 'number':
                                                    case 'password':
                                                        const hintText = i18n.translate({
                                                            id: `field.sourceConfig.${key}.hint`,
                                                            sourceName: 'KnowledgebaseSources',
                                                        });
                                                        const hasHint =
                                                            hintText && !hintText.startsWith('field.sourceConfig.');

                                                        return (
                                                            <div key={key}>
                                                                <label htmlFor={`config-${key}`} className="block mb-2">
                                                                    {i18n.translate({
                                                                        id: `field.sourceConfig.${key}`,
                                                                        sourceName: 'KnowledgebaseSources',
                                                                    })}
                                                                </label>
                                                                <Input
                                                                    inputType={sourceConfiguration.type}
                                                                    id={`config-${key}`}
                                                                    value={
                                                                        editingSource.configuration?.[key] ||
                                                                        sourceConfiguration.defaultValue ||
                                                                        ''
                                                                    }
                                                                    hasVisibilityButton={
                                                                        sourceConfiguration.type === 'password'
                                                                    }
                                                                    required={sourceConfiguration.required}
                                                                    {...(sourceConfiguration.placeholder
                                                                        ? {
                                                                              placeholder: i18n.translate({
                                                                                  id: `field.sourceConfigPlaceholder.${key}`,
                                                                                  sourceName: 'KnowledgebaseSources',
                                                                              }),
                                                                          }
                                                                        : {})}
                                                                    {...(sourceConfiguration.minNumber
                                                                        ? {
                                                                              inputProps: {
                                                                                  min: sourceConfiguration.minNumber,
                                                                              },
                                                                          }
                                                                        : {})}
                                                                    onChange={(value) =>
                                                                        handleSourceChange(editingSource, {
                                                                            configuration: {
                                                                                ...editingSource.configuration,
                                                                                [key]: value,
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                                {hasHint && (
                                                                    <p
                                                                        className="mt-1 text-sm text-gray-500"
                                                                        dangerouslySetInnerHTML={{ __html: hintText }}
                                                                    />
                                                                )}
                                                            </div>
                                                        );
                                                    case 'boolean':
                                                        const checkboxHintText = i18n.translate({
                                                            id: `field.sourceConfig.${key}.hint`,
                                                            sourceName: 'KnowledgebaseSources',
                                                        });
                                                        const hasCheckboxHint =
                                                            checkboxHintText &&
                                                            !checkboxHintText.startsWith('field.sourceConfig.');

                                                        return (
                                                            <div key={key}>
                                                                <label htmlFor={`config-${key}`} className="block mb-2">
                                                                    {i18n.translate({
                                                                        id: `field.sourceConfig.${key}`,
                                                                        sourceName: 'KnowledgebaseSources',
                                                                    })}
                                                                </label>
                                                                <Checkbox
                                                                    name={`config-${key}`}
                                                                    checked={
                                                                        editingSource.configuration?.[key] ??
                                                                        sourceConfiguration.defaultValue ??
                                                                        false
                                                                    }
                                                                    required={sourceConfiguration.required}
                                                                    onChange={(e) =>
                                                                        handleSourceChange(editingSource, {
                                                                            configuration: {
                                                                                ...editingSource.configuration,
                                                                                [key]: e.target.checked,
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                                {hasCheckboxHint && (
                                                                    <p
                                                                        className="mt-1 text-sm text-gray-500"
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: checkboxHintText,
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        );
                                                    case 'upload':
                                                        return (
                                                            <div
                                                                key={key}
                                                                {...getRootProps()}
                                                                className={`mt-4 border-2 border-dashed border-input-borderColor rounded-lg p-6 text-center cursor-pointer ${
                                                                    isDragActive
                                                                        ? 'border-primary'
                                                                        : 'hover:border-primary'
                                                                } transition-all duration-100`}
                                                            >
                                                                <input {...getInputProps()} disabled={demo} />
                                                                <div>
                                                                    <p className="mb-2">
                                                                        {i18n.translate({
                                                                            id: 'documents.drag',
                                                                            sourceName: 'Knowledgebase',
                                                                        })}
                                                                    </p>
                                                                    <Button>
                                                                        {i18n.translate({
                                                                            id: 'button.upload',
                                                                            sourceName: 'Knowledgebase',
                                                                        })}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        );
                                                }
                                            })}
                                    </div>

                                    {editingSource.type !== 'manual' && (
                                        <>
                                            <Button
                                                variant={'secondary'}
                                                className="mt-5"
                                                onClick={() => sourceActions.validateSource(editingSource)}
                                            >
                                                {i18n.translate({
                                                    id: 'button.validate',
                                                    sourceName: 'KnowledgebaseSources',
                                                })}
                                            </Button>

                                            <div className="border-t border-layout-borderColor mt-5 pt-3">
                                                <h4 className="mb-5">
                                                    {i18n.translate({
                                                        id: 'headline.synchronizationSettings',
                                                        sourceName: 'KnowledgebaseSources',
                                                    })}
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label htmlFor="sourceInterval" className="block mb-2">
                                                            {i18n.translate({
                                                                id: 'field.sourceInterval',
                                                                sourceName: 'KnowledgebaseSources',
                                                            })}
                                                        </label>
                                                        <Select
                                                            id="sourceInterval"
                                                            onChange={(value) =>
                                                                handleSourceChange(editingSource, { interval: value })
                                                            }
                                                            value={editingSource.interval || ''}
                                                        >
                                                            {Object.entries(
                                                                sourceProcessors[
                                                                    editingSource.type as keyof typeof sourceProcessors
                                                                ].intervals,
                                                            ).map(([_, intervalValue]) => (
                                                                <option
                                                                    key={String(intervalValue)}
                                                                    value={String(intervalValue)}
                                                                >
                                                                    {i18n.translate({
                                                                        id: `field.sourceInterval.option.${intervalValue}`,
                                                                        sourceName: 'KnowledgebaseSources',
                                                                    })}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </div>
                                                </div>

                                                <Button
                                                    variant={'secondary'}
                                                    className="mt-5"
                                                    onClick={() => sourceActions.syncSourceNow(editingSource)}
                                                    disabled={demo}
                                                >
                                                    {i18n.translate({
                                                        id: 'button.syncNow',
                                                        sourceName: 'KnowledgebaseSources',
                                                    })}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <PaginatedFileList<Document | { name: string; sha1: string }>
                                    title={i18n.translate({
                                        id: 'list.selectedDocuments',
                                        sourceName: 'Knowledgebase',
                                    })}
                                    isNew={!(id && id !== 'new')}
                                    selectedDocumentCount={editingSource.documentCount ?? 0}
                                    listType={PaginationListType.KnowledgebaseExistingFiles}
                                    renderItem={(document, index: number) => {
                                        if ((document as Document).identifier) {
                                            return (
                                                <FileListItem
                                                    key={(document as Document).identifier}
                                                    document={document as Document}
                                                    handleReembedDocument={handleReembedDocument}
                                                    source={editingSource}
                                                    handleRemoveExistingDocument={handleRemoveExistingDocument}
                                                />
                                            );
                                        }
                                    }}
                                    itemsFunction={getKnowledgebaseDocuments}
                                />

                                <div className="text-right space-x-3 mt-6">
                                    <Button
                                        variant={'secondary'}
                                        onClick={() => sourceActions.deleteSource(editingSource)}
                                        disabled={demo}
                                    >
                                        {i18n.translate({ id: 'button.delete', sourceName: 'KnowledgebaseSources' })}
                                    </Button>

                                    <Button variant={'secondary'} onClick={() => handleCloseSource(editingSource)}>
                                        {i18n.translate({ id: 'button.cancel', sourceName: 'KnowledgebaseSources' })}
                                    </Button>

                                    <Button
                                        variant={!sourceHasChanges ? 'disabled' : 'primary'}
                                        onClick={() => sourceActions.editSource(editingSource)}
                                        disabled={demo || !sourceHasChanges}
                                    >
                                        {i18n.translate({ id: 'button.save', sourceName: 'KnowledgebaseSources' })}
                                    </Button>
                                </div>
                            </AdminBox>
                        );
                    })()}

                {!isEditingSource.status && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <AdminBox contentLoaded={isLoaded}>
                                <AdminBoxHeadline
                                    headLine={
                                        isEditMode
                                            ? i18n.translate({ id: 'title.form.edit', sourceName: 'Knowledgebase' })
                                            : i18n.translate({ id: 'title.form.create', sourceName: 'Knowledgebase' })
                                    }
                                    subLine={id !== 'new' ? knowledgebaseSnapshot?.name : undefined}
                                />

                                <label htmlFor="name" className="flex flex-col gap-2">
                                    <h4>{i18n.translate({ id: 'form.name', sourceName: 'Knowledgebase' })}</h4>
                                    <span className="block my-2 text-md text-gray-500">
                                        {i18n.translate({ id: 'form.name.hint', sourceName: 'Knowledgebase' })}
                                    </span>
                                    <Input
                                        inputType="text"
                                        value={formData.name}
                                        onChange={(value) => handleChange('name', value)}
                                        required
                                        id="name"
                                    />
                                </label>

                                <label htmlFor="description" className="flex flex-col gap-2">
                                    <h4>{i18n.translate({ id: 'form.description', sourceName: 'Knowledgebase' })}</h4>
                                    <span className="block my-2 text-md text-gray-500">
                                        {i18n.translate({ id: 'form.description.hint', sourceName: 'Knowledgebase' })}
                                    </span>
                                    <TextareaAutosize
                                        id="description"
                                        className="bg-input-backgroundColor w-full p-3 border border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100"
                                        value={formData.description}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        minRows={4}
                                        maxRows={8}
                                    />
                                </label>

                                <div className="mt-5 text-right">
                                    <Button
                                        variant={isSaving ? 'secondary' : 'primary'}
                                        onClick={handleSubmit}
                                        disabled={demo || isSaving}
                                    >
                                        {isSaving ? (
                                            <div className="flex items-center gap-2">
                                                <Spinner size="sm" />
                                                {i18n.translate({ id: 'saving', sourceName: 'Knowledgebase' })}
                                            </div>
                                        ) : isEditMode ? (
                                            i18n.translate({ id: 'save', sourceName: 'Main' })
                                        ) : (
                                            i18n.translate({ id: 'form.create', sourceName: 'Knowledgebase' })
                                        )}
                                    </Button>
                                </div>
                            </AdminBox>

                            <AdminBox contentLoaded={isLoaded}>
                                <div
                                    className={`flex flex-col justify-between h-full gap-4 ${
                                        id === 'new' ? 'pointer-events-none opacity-40' : ''
                                    }`}
                                >
                                    <div className="flex flex-col gap-2">
                                        <h4>{i18n.translate({ id: 'source.create', sourceName: 'Knowledgebase' })}</h4>
                                        <label htmlFor="sourceType" className="block mb-2">
                                            {i18n.translate({
                                                id: 'field.sourceType',
                                                sourceName: 'KnowledgebaseSources',
                                            })}
                                        </label>
                                        <Select
                                            id="sourceType"
                                            onChange={(value) => handleTempSource('type', value)}
                                            value={tempSource.type || ''}
                                        >
                                            <option disabled key="" value="">
                                                {i18n.translate({
                                                    id: 'field.sourceType.option.select',
                                                    sourceName: 'KnowledgebaseSources',
                                                })}
                                            </option>
                                            {Object.entries(sourceProcessors || {}).map(([type, processor]) => {
                                                if (!processor) return null;
                                                const isLimitReached =
                                                    processor.maxByType !== null &&
                                                    knowledgebaseSources.filter((source) => source.type === type)
                                                        .length >= processor.maxByType;
                                                return (
                                                    <option key={type} value={type} disabled={isLimitReached}>
                                                        {i18n.translate({
                                                            id: `field.sourceType.option.${type}`,
                                                            sourceName: 'KnowledgebaseSources',
                                                        })}
                                                        {isLimitReached
                                                            ? ` (${i18n.translate({
                                                                  id: `field.sourceType.limitReached`,
                                                                  sourceName: 'KnowledgebaseSources',
                                                              })})`
                                                            : ''}
                                                    </option>
                                                );
                                            })}
                                        </Select>
                                    </div>
                                    {!demo && (
                                        <div className="text-right">
                                            <Button
                                                variant={tempSource.type ? 'primary' : 'disabled'}
                                                onClick={() => sourceActions.editSource(tempSource, true)}
                                                disabled={!tempSource.type || isSaving}
                                            >
                                                {i18n.translate({
                                                    id: 'button.create',
                                                    sourceName: 'KnowledgebaseSources',
                                                })}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </AdminBox>
                        </div>

                        {knowledgebaseSources.length > 0 && !demo && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-3">
                                {knowledgebaseSources.map((source) => (
                                    <AdminBox contentLoaded={isLoadedSources} key={source.id} classString={'mb-0'}>
                                        <div className="border-b border-layout-borderColor pb-4 mb-4">
                                            <div className="flex flex-direction-col items-center gap-6">
                                                {(() => {
                                                    const processor = sourceProcessors?.[source.type as keyof typeof sourceProcessors];
                                                    const iconName = processor?.icon;
                                                    const PhosphorIconComponent = iconName
                                                        ? (PhosphorIcons[iconName as keyof typeof PhosphorIcons] as PhosphorIcons.Icon)
                                                        : null;
                                                    return (
                                                        <div
                                                            className={
                                                                'text-body rounded-button-border-radius bg-button-secondary-background text-button-secondary-text flex items-center justify-center !h-[50px] !w-[50px]'
                                                            }
                                                        >
                                                            {PhosphorIconComponent && (
                                                                <PhosphorIconComponent size={25} />
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                                <div className="overflow-auto">
                                                    <h4 className="truncate">{source.name}</h4>
                                                    <div className="flex items-center space-x-2 text-md">
                                                        <PhosphorIcons.TagIcon />
                                                        <span>
                                                            {i18n.translate({
                                                                id: `field.sourceType.option.${source.type}`,
                                                                sourceName: 'KnowledgebaseSources',
                                                            })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 mb-4 relative">
                                            <div>
                                                <h5 className="flex flex-row gap-4 items-center mb-3">
                                                    <PhosphorIcons.GearIcon />
                                                    {i18n.translate({
                                                        id: 'headline.configuration',
                                                        sourceName: 'KnowledgebaseSources',
                                                    })}
                                                </h5>
                                                <div className="flex flex-row mt-2 text-sm text-gray-500 space-x-2 items-center">
                                                    <div
                                                        className={`w-3 h-3 rounded-full ${
                                                            source.status === 'sync'
                                                                ? 'bg-orange-400'
                                                                : source.status === 'error'
                                                                  ? 'bg-red-500'
                                                                  : 'bg-green-500'
                                                        }`}
                                                    ></div>
                                                    <span>
                                                        {i18n.translate({
                                                            id: `source.status.${source.status}`,
                                                            sourceName: 'KnowledgebaseSources',
                                                        })}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row mt-2 text-sm text-gray-500 space-x-2 items-center">
                                                    <PhosphorIcons.ArrowsClockwiseIcon />
                                                    <span>
                                                        {source.type === 'manual'
                                                            ? i18n.translate({
                                                                  id: 'source.interval.manual',
                                                                  sourceName: 'KnowledgebaseSources',
                                                              })
                                                            : i18n.translate({
                                                                  id: 'source.interval.automatic',
                                                                  sourceName: 'KnowledgebaseSources',
                                                              })}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row mt-2 text-sm text-gray-500 space-x-2 items-center">
                                                    <PhosphorIcons.TimerIcon />
                                                    <span>
                                                        {i18n.translate({
                                                            id: 'field.sourceInterval',
                                                            sourceName: 'KnowledgebaseSources',
                                                        })}
                                                        :{' '}
                                                        {source.type === 'manual'
                                                            ? '-'
                                                            : i18n.translate({
                                                                  id: `field.sourceInterval.option.${source.interval}`,
                                                                  sourceName: 'KnowledgebaseSources',
                                                              })}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row mt-2 text-sm text-gray-500 space-x-2 items-center">
                                                    <PhosphorIcons.ClockCounterClockwiseIcon />
                                                    <span>
                                                        {i18n.translate({
                                                            id: 'source.sync.last',
                                                            sourceName: 'KnowledgebaseSources',
                                                        })}
                                                        :{' '}
                                                        {source.lastExecution
                                                            ? new Date(source.lastExecution).toLocaleString(undefined, {
                                                                  day: '2-digit',
                                                                  month: '2-digit',
                                                                  year: 'numeric',
                                                                  hour: '2-digit',
                                                                  minute: '2-digit',
                                                              })
                                                            : '-'}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row mt-2 text-sm text-gray-500 space-x-2 items-center">
                                                    <PhosphorIcons.ClockCountdownIcon />
                                                    <span>
                                                        {i18n.translate({
                                                            id: 'source.sync.next',
                                                            sourceName: 'KnowledgebaseSources',
                                                        })}
                                                        :{' '}
                                                        {source.nextExecution
                                                            ? new Date(source.nextExecution).toLocaleString(undefined, {
                                                                  day: '2-digit',
                                                                  month: '2-digit',
                                                                  year: 'numeric',
                                                                  hour: '2-digit',
                                                                  minute: '2-digit',
                                                              })
                                                            : '-'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <h5 className="flex flex-row gap-4 items-center mb-3">
                                                    <PhosphorIcons.FileIcon />
                                                    {i18n.translate({
                                                        id: 'headline.files',
                                                        sourceName: 'KnowledgebaseSources',
                                                    })}
                                                </h5>
                                                <div className="flex flex-row mt-2 text-sm text-gray-500 space-x-2 items-center">
                                                    <div
                                                        className={`w-3 h-3 rounded-full ${
                                                            source.documentStatus === 'in_progress'
                                                                ? 'bg-orange-400'
                                                                : source.documentStatus === 'error'
                                                                  ? 'bg-red-500'
                                                                  : 'bg-green-500'
                                                        }`}
                                                    ></div>
                                                    <span>
                                                        {i18n.translate({
                                                            id: `status.${source.documentStatus}`,
                                                            sourceName: 'Knowledgebases',
                                                        })}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row mt-2 text-sm text-gray-500 space-x-2 items-center">
                                                    {i18n.translate({
                                                        id: 'text.documents',
                                                        sourceName: 'KnowledgebaseSources',
                                                        params: { length: source.documentCount },
                                                    })}
                                                </div>
                                            </div>

                                            <div
                                                className="hidden md:block absolute left-1/2 top-0 h-full border-l border-layout-borderColor"
                                                style={{ transform: 'translateX(-50%)' }}
                                            />
                                        </div>
                                        <div className="flex items-center space-x-2 justify-end gap-3">
                                            {source.type !== 'manual' && (
                                                <button
                                                    onClick={async () => sourceActions.syncSourceNow(source)}
                                                    className="tooltip leading-[25px] transition duration-200 ease-in-out font-normal focus:outline-none h-[35px] w-[35px] text-body !p-0 !m-0 flex justify-center items-center  rounded-full bg-button-secondary-background group-box:bg-button-secondary-box-background text-button-secondary-text hover:bg-button-secondary-background-hover group-box:hover:bg-button-secondary-box-background-hover hover:text-button-secondary-text-hover"
                                                    data-tooltip-content={i18n.translate({
                                                        id: 'button.syncNow',
                                                        sourceName: 'KnowledgebaseSources',
                                                    })}
                                                    disabled={demo}
                                                >
                                                    <PhosphorIcons.ArrowsClockwiseIcon size={20} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => sourceActions.deleteSource(source)}
                                                className="tooltip leading-[25px] transition duration-200 ease-in-out font-normal focus:outline-none h-[35px] w-[35px] text-body !p-0 !m-0 flex justify-center items-center  rounded-full bg-button-secondary-background group-box:bg-button-secondary-box-background text-button-secondary-text hover:bg-button-secondary-background-hover group-box:hover:bg-button-secondary-box-background-hover hover:text-button-secondary-text-hover"
                                                data-tooltip-content={i18n.translate({
                                                    id: 'button.delete',
                                                    sourceName: 'KnowledgebaseSources',
                                                })}
                                                disabled={demo}
                                            >
                                                <PhosphorIcons.TrashIcon size={20} />
                                            </button>
                                            <button
                                                onClick={() => handleEditSource(source)}
                                                className="tooltip leading-[25px] transition duration-200 ease-in-out font-normal focus:outline-none h-[35px] w-[35px] text-body !p-0 !m-0 flex justify-center items-center  rounded-full bg-button-secondary-background group-box:bg-button-secondary-box-background text-button-secondary-text hover:bg-button-secondary-background-hover group-box:hover:bg-button-secondary-box-background-hover hover:text-button-secondary-text-hover"
                                                data-tooltip-content={i18n.translate({
                                                    id: 'button.edit',
                                                    sourceName: 'KnowledgebaseSources',
                                                })}
                                            >
                                                <PhosphorIcons.PencilSimpleIcon size={20} />
                                            </button>
                                        </div>
                                    </AdminBox>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </AdminContainer>
        </div>
    );
};

export default Knowledgebase;
